import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronDown, ChevronRight } from 'lucide-react';
import axios from '../utils/axiosInstance';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '../components/ui/table';
import { localeLabel } from '../utils/localeFlag';

const TRANSLATABLE_TYPES = new Set([
  'single_line_text_field',
  'multi_line_text_field',
  'rich_text_field',
]);

// Phase 22 — same text-vocab as Theme Strings + Metafield Detail so users see
// one consistent "is this pushed?" signal across every translatable surface.
const STATUS_STYLES = {
  pushed:     { className: 'bg-emerald-100 text-emerald-800 hover:bg-emerald-100', label: 'Pushed' },
  translated: { className: 'bg-blue-100 text-blue-800 hover:bg-blue-100',          label: 'Translated' },
  pending:    { className: 'bg-gray-100 text-gray-800 hover:bg-gray-100',          label: 'Pending' },
  partial:    { className: 'bg-orange-100 text-orange-800 hover:bg-orange-100',    label: 'Partial' },
  failed:     { className: 'bg-red-100 text-red-800 hover:bg-red-100',             label: 'Failed' },
  missing:    { className: 'bg-muted text-muted-foreground',                       label: '—' },
};

function StatusBadge({ locale, status }) {
  const style = STATUS_STYLES[status] || STATUS_STYLES.missing;
  return (
    <Badge className={`${style.className} text-xs`} title={`${locale.toUpperCase()}: ${status}`}>
      {style.label}
    </Badge>
  );
}

function fieldDefaultPolicy(fieldType) {
  return TRANSLATABLE_TYPES.has(fieldType) ? 'translate' : 'skip';
}

function effectivePolicy(field, fieldPolicies) {
  const explicit = (fieldPolicies || {})[field.key];
  if (explicit === 'translate' || explicit === 'skip') return explicit;
  return fieldDefaultPolicy(field.type);
}

function compactValue(value) {
  if (value == null) return '—';
  const str = typeof value === 'string' ? value : String(value);
  // Rich-text JSON values are noisy; trim and tag.
  if (str.startsWith('{"type":"root"')) {
    try {
      const parsed = JSON.parse(str);
      const text = (parsed.children || [])
        .flatMap((p) => (p.children || []).map((c) => c.value || ''))
        .filter(Boolean)
        .join(' ');
      return text.length > 120 ? text.slice(0, 120) + '…' : text || '(rich text)';
    } catch {
      return '(rich text)';
    }
  }
  return str.length > 120 ? str.slice(0, 120) + '…' : str;
}

export default function MetaobjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [definition, setDefinition] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [expandedEntry, setExpandedEntry] = useState(null);
  const [policySaving, setPolicySaving] = useState(false);
  const [policyError, setPolicyError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    axios
      .get(`/api/transly/metaobjects/definitions/${id}/`)
      .then((res) => {
        if (cancelled) return;
        setDefinition(res.data.definition);
        setEntries(res.data.entries || []);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.response?.data?.error || err.message || 'Failed to load metaobject');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const setTranslationPolicy = async (newPolicy) => {
    if (definition?.translation_policy === newPolicy) return;
    setPolicySaving(true);
    setPolicyError(null);
    try {
      const res = await axios.patch(
        `/api/transly/metaobjects/definitions/${id}/`,
        { translation_policy: newPolicy }
      );
      setDefinition(res.data.definition);
    } catch (err) {
      setPolicyError(err.response?.data?.error || err.message || 'Failed to update policy');
    } finally {
      setPolicySaving(false);
    }
  };

  const setFieldPolicy = async (fieldKey, newPolicy) => {
    setPolicySaving(true);
    setPolicyError(null);
    try {
      const current = { ...(definition?.field_policies || {}) };
      if (newPolicy === null) {
        delete current[fieldKey];
      } else {
        current[fieldKey] = newPolicy;
      }
      const res = await axios.patch(
        `/api/transly/metaobjects/definitions/${id}/`,
        { field_policies: current }
      );
      setDefinition(res.data.definition);
    } catch (err) {
      setPolicyError(err.response?.data?.error || err.message || 'Failed to update field policy');
    } finally {
      setPolicySaving(false);
    }
  };

  const filteredEntries = useMemo(() => {
    let result = entries;
    if (search) {
      const term = search.toLowerCase();
      result = result.filter((e) =>
        (e.entry_handle || '').toLowerCase().includes(term) ||
        e.fields.some((f) => (f.raw_value || '').toLowerCase().includes(term))
      );
    }
    if (statusFilter !== 'all') {
      result = result.filter((e) => {
        const statuses = Object.values(e.status || {});
        if (statusFilter === 'untranslated') {
          return statuses.some((s) => s !== 'pushed');
        }
        if (statusFilter === 'pushed') {
          return statuses.length > 0 && statuses.every((s) => s === 'pushed');
        }
        return true;
      });
    }
    return result;
  }, [entries, search, statusFilter]);

  const localeKeys = useMemo(() => {
    if (!entries.length) return [];
    return Object.keys(entries[0].status || {}).sort();
  }, [entries]);

  const fieldSchema = Array.isArray(definition?.field_schema) ? definition.field_schema : [];

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <button
        onClick={() => navigate(`/metaobjects`)}
        className="text-sm text-muted-foreground hover:text-foreground cursor-pointer flex items-center gap-1 mb-4"
      >
        <ChevronLeft className="h-4 w-4" />
        Metaobjects
      </button>

      {definition && (
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-foreground">{definition.name}</h1>
            <p className="text-sm text-muted-foreground font-mono">
              {definition.type} · {entries.length} entries · {fieldSchema.length} fields
            </p>
          </div>
          <Input
            type="text"
            placeholder="Search entries..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-64"
          />
        </div>
      )}

      {/* Translation policy panel */}
      {definition && (
        <div className="border border-border rounded-md p-4 mb-6 bg-muted/30">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="text-sm font-medium text-foreground mb-1">Translation policy</div>
              <p className="text-xs text-muted-foreground">
                Definition-level setting. Skip excludes this metaobject from the bulk translation pipeline.
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                variant={definition.translation_policy === 'translate' ? 'default' : 'outline'}
                size="sm"
                disabled={policySaving}
                onClick={() => setTranslationPolicy('translate')}
              >
                Translate
              </Button>
              <Button
                variant={definition.translation_policy === 'skip' ? 'default' : 'outline'}
                size="sm"
                disabled={policySaving}
                onClick={() => setTranslationPolicy('skip')}
              >
                Skip
              </Button>
              <Button
                variant={!definition.translation_policy ? 'default' : 'outline'}
                size="sm"
                disabled={policySaving}
                onClick={() => setTranslationPolicy(null)}
              >
                Unset
              </Button>
            </div>
          </div>

          <div className="mt-3 flex items-center gap-3 text-xs text-muted-foreground">
            <span>Translated rows (DE+ES): <strong>{definition.translated_count ?? 0}</strong></span>
            <span>·</span>
            <span>Pushed to Shopify: <strong>{definition.pushed_count ?? 0}</strong></span>
            {policySaving && <span className="text-foreground">Saving…</span>}
            {policyError && <span className="text-destructive">{policyError}</span>}
          </div>
        </div>
      )}

      {/* Field-level policy table */}
      {definition && fieldSchema.length > 0 && (
        <div className="border border-border rounded-md p-4 mb-6">
          <div className="text-sm font-medium text-foreground mb-1">Per-field policy</div>
          <p className="text-xs text-muted-foreground mb-3">
            Override the per-field decision. Default follows the field's data type
            (text fields translate, refs/colors/files skip).
          </p>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Field</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Effective</TableHead>
                <TableHead>Override</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {fieldSchema.map((f) => {
                const explicit = (definition.field_policies || {})[f.key];
                const effective = effectivePolicy(f, definition.field_policies);
                return (
                  <TableRow key={f.key}>
                    <TableCell>
                      <div className="font-mono text-xs">{f.key}</div>
                      <div className="text-xs text-muted-foreground">{f.name}</div>
                    </TableCell>
                    <TableCell className="font-mono text-xs">{f.type}</TableCell>
                    <TableCell>
                      <span className={effective === 'translate' ? 'text-emerald-600' : 'text-muted-foreground'}>
                        {effective}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant={explicit === 'translate' ? 'default' : 'outline'}
                          disabled={policySaving}
                          onClick={() => setFieldPolicy(f.key, 'translate')}
                        >
                          Translate
                        </Button>
                        <Button
                          size="sm"
                          variant={explicit === 'skip' ? 'default' : 'outline'}
                          disabled={policySaving}
                          onClick={() => setFieldPolicy(f.key, 'skip')}
                        >
                          Skip
                        </Button>
                        <Button
                          size="sm"
                          variant={!explicit ? 'default' : 'outline'}
                          disabled={policySaving}
                          onClick={() => setFieldPolicy(f.key, null)}
                        >
                          Default
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Entry list */}
      {error && (
        <div className="flex flex-col items-center py-12">
          <p className="text-sm text-destructive mb-4">{error}</p>
        </div>
      )}

      {loading && !error && (
        <div className="space-y-2">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="flex gap-4 px-2 py-3">
              <div className="animate-pulse bg-muted h-4 rounded w-48" />
              <div className="animate-pulse bg-muted h-4 rounded w-16" />
            </div>
          ))}
        </div>
      )}

      {!loading && !error && entries.length === 0 && (
        <p className="text-sm text-muted-foreground text-center py-8">
          No entries cached. Run a sync (or set the policy off "skip") to populate.
        </p>
      )}

      {/* Entry status filter */}
      {!loading && !error && entries.length > 0 && localeKeys.length > 0 && (
        <div className="flex gap-1 mb-3">
          <Button
            variant={statusFilter === 'all' ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => setStatusFilter('all')}
          >
            All ({entries.length})
          </Button>
          <Button
            variant={statusFilter === 'untranslated' ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => setStatusFilter('untranslated')}
          >
            Untranslated ({entries.filter((e) => Object.values(e.status || {}).some((s) => s !== 'pushed')).length})
          </Button>
          <Button
            variant={statusFilter === 'pushed' ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => setStatusFilter('pushed')}
          >
            Fully pushed ({entries.filter((e) => {
              const s = Object.values(e.status || {});
              return s.length > 0 && s.every((v) => v === 'pushed');
            }).length})
          </Button>
        </div>
      )}

      {!loading && !error && filteredEntries.length > 0 && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Entry</TableHead>
              <TableHead className="w-20 text-right text-muted-foreground">Fields</TableHead>
              {/* Phase 22: per-locale columns so each "Pushed" badge sits under
                  its locale flag header — same pattern as MetafieldDetail and
                  ThemeStrings. */}
              {localeKeys.length === 0 ? (
                <TableHead>Translation status</TableHead>
              ) : (
                localeKeys.map((loc) => (
                  <TableHead key={loc} className="w-28">{localeLabel(loc)}</TableHead>
                ))
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredEntries.map((entry) => {
              const isExpanded = expandedEntry === entry.entry_gid;
              const status = entry.status || {};
              const colSpan = 2 + Math.max(localeKeys.length, 1);
              return (
                <React.Fragment key={entry.entry_gid}>
                  <TableRow
                    className="cursor-pointer"
                    onClick={() => setExpandedEntry(isExpanded ? null : entry.entry_gid)}
                  >
                    <TableCell className="flex items-center gap-2">
                      {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      <span className="font-mono text-xs">{entry.entry_handle || entry.entry_gid}</span>
                    </TableCell>
                    <TableCell className="text-right text-muted-foreground text-sm">
                      {entry.fields.length}
                    </TableCell>
                    {localeKeys.length === 0 ? (
                      <TableCell>
                        <span className="text-xs text-muted-foreground">no policy</span>
                      </TableCell>
                    ) : (
                      localeKeys.map((loc) => (
                        <TableCell key={loc}>
                          <StatusBadge locale={loc} status={status[loc] || 'missing'} />
                        </TableCell>
                      ))
                    )}
                  </TableRow>
                  {isExpanded && (
                    <TableRow>
                      <TableCell colSpan={colSpan} className="bg-muted/30">
                        <div className="space-y-1 py-2">
                          {entry.fields.map((f) => (
                            <div key={f.field_key} className="flex gap-3 text-xs">
                              <span className="font-mono text-muted-foreground w-40 shrink-0">{f.field_key}</span>
                              <span className="flex-1">{compactValue(f.raw_value)}</span>
                            </div>
                          ))}
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
